import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { DocumentViewerComponent } from 'src/app/global/components/common/document-viewer/modal-image.component';
import { PdfThumbnailService } from 'src/app/global/utils/pdf-thumbnail/pdf-thumbnail.service';
import { languageSelector, organisationConfigurationSelector } from 'src/app/global/store/global.selectors';
import { DenialReason } from 'src/app/global/services/config-all/config-all.model';
import { UserRequestDenyDialogComponent } from 'src/app/user/dialogs/user-request-deny-dialog/user-request-deny-dialog.component';
import { State } from 'src/app/store';
import { ResidenceService } from '../../service/residence.abstract.service';
import { Residence, ResidenceSubType, isRenewalReview, isNewClaimReview, getReviewableProofDocuments } from '../../residence.model';
import { UserUtilService } from 'src/app/user/util/user-util.service';

export interface ReviewResidenceDialogData {
    residence: Residence;
    citizen?: {
        id?: string;
        firstName?: string;
        lastName?: string;
        email?: string;
        phone?: string;
        picture?: string;
        language?: string;
    };
}

@Component({
    selector: 'app-review-residence-dialog',
    templateUrl: './review-residence-dialog.component.html',
    styleUrls: ['./review-residence-dialog.component.scss'],
})
export class ReviewResidenceDialogComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    public loading = false;
    public showRejectForm = false;
    public showApprovalGuide = false;
    public approvalGuidelines = '';
    public supportingDocuments = '';
    private denialReasons: DenialReason[] = [];
    private currentLang: string;
    private orgLang: any;
    public selectedSubType: { value: ResidenceSubType; label: string } | null = null;
    public pdfThumbnailState: { [url: string]: string | 'error' } = {};

    public subTypeOptions: { value: ResidenceSubType; label: string }[] = [
        { value: 'RESIDENT', label: this.translateService.instant('pages.residences.subType.resident') },
        { value: 'NONRESIDENT', label: this.translateService.instant('pages.residences.subType.nonResident') },
    ];

    public rejectForm = this.formBuilder.group({
        notes: new FormControl('', [Validators.required]),
    });

    constructor(
        private formBuilder: FormBuilder,
        private residenceService: ResidenceService,
        private messageService: MessageService,
        private translateService: TranslateService,
        private dialog: MatDialog,
        private store: Store<State>,
        private userUtilService: UserUtilService,
        private pdfThumbnailService: PdfThumbnailService,
        public dialogRef: MatDialogRef<ReviewResidenceDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ReviewResidenceDialogData,
    ) {
        this.selectedSubType = null;
    }

    ngOnInit(): void {
        this.loadPdfThumbnails();

        this.store
            .select(languageSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((lang) => {
                this.currentLang = lang;
            });

        this.store
            .select(organisationConfigurationSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((config) => {
                const lang = this.currentLang || this.translateService.currentLang || 'fr';
                this.orgLang = config?.lang;
                this.approvalGuidelines = config?.residence?.approvalGuidelines?.[lang] || '';
                this.supportingDocuments = config?.residence?.supportingDocuments?.[lang] || '';
                this.denialReasons = config?.residence?.denialReasons?.[lang] || [];
            });
    }

    get citizenName(): string {
        const c = this.data.citizen;
        if (c?.firstName || c?.lastName) {
            return `${c.firstName || ''} ${c.lastName || ''}`.trim();
        }
        return '';
    }

    get addressLine1(): string {
        const addr = this.data.residence.address;
        if (!addr) return '';
        const parts = [];
        if (addr.unit) parts.push(`#${addr.unit}`);
        if (addr.number) parts.push(addr.number);
        if (addr.streetName || addr.street) parts.push(addr.streetName || addr.street);
        return parts.join(' ');
    }

    get addressLine2(): string {
        const addr = this.data.residence.address;
        if (!addr) return '';
        return [addr.city, addr.province, addr.postCode].filter(Boolean).join(', ');
    }

    get notes(): FormControl {
        return this.rejectForm.get('notes') as FormControl;
    }

    get proofDocuments(): string[] {
        return getReviewableProofDocuments(this.data.residence);
    }

    get previousRejectionNote(): string | null {
        return this.data.residence.verification?.notes;
    }

    get isRenewal(): boolean {
        return isRenewalReview(this.data.residence);
    }

    get isNewClaim(): boolean {
        return isNewClaimReview(this.data.residence);
    }

    get showSubTypeSelector(): boolean {
        return this.isNewClaim;
    }

    get expirationDate(): string | null {
        return this.data.residence.expirationDate;
    }

    get citizenProfileLink(): string {
        return this.data.citizen?.id ? `/user-update/${this.data.citizen.id}` : '';
    }

    isPdf(url: string): boolean {
        const path = url.split('?')[0];
        return path.toLowerCase().endsWith('.pdf');
    }

    docState(doc: string): 'image' | 'pdf-thumb' | 'pdf-loading' | 'pdf-error' {
        if (!this.isPdf(doc)) {
            return 'image';
        }
        const state = this.pdfThumbnailState[doc];
        if (state === 'error') {
            return 'pdf-error';
        }
        return state ? 'pdf-thumb' : 'pdf-loading';
    }

    private loadPdfThumbnails(): void {
        this.proofDocuments
            .filter((doc) => this.isPdf(doc))
            .forEach((doc) => {
                this.pdfThumbnailService
                    .generateThumbnail(doc)
                    .then((thumbnail) => {
                        this.pdfThumbnailState[doc] = thumbnail;
                    })
                    .catch(() => {
                        this.pdfThumbnailState[doc] = 'error';
                    });
            });
    }

    openDocument(url: string): void {
        this.dialog.open(DocumentViewerComponent, {
            data: { src: url },
            maxWidth: '90vw',
            maxHeight: '90vh',
        });
    }

    private submitReview(dto: { status: 'APPROVED' | 'REJECTED'; notes?: string }): void {
        const call = this.isRenewal ? this.residenceService.reviewRenewal(this.data.residence._id, dto) : this.residenceService.reviewResidence(this.data.residence._id, dto);
        call.pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                const toastKey = dto.status === 'APPROVED' ? 'pages.residences.toast.approveSuccess' : 'pages.residences.toast.rejectSuccess';
                this.messageService.success(this.translateService.instant(toastKey));
                this.dialogRef.close(true);
            },
            error: (error) => {
                this.messageService.error(this.userUtilService.resolveApiErrorMessage(error, 'pages.residences.toast.error'));
                this.loading = false;
            },
        });
    }

    approve(): void {
        this.loading = true;
        const dto: any = { status: 'APPROVED' };
        if (this.isNewClaim) {
            dto.subType = this.selectedSubType?.value;
        }
        this.submitReview(dto);
    }

    handleReject(): void {
        let langLabel = this.translateService.instant(`common.langs.${this.orgLang?.default || 'fr'}`);
        if (this.data.citizen?.language && this.orgLang?.available?.includes(this.data.citizen.language)) {
            langLabel = this.translateService.instant(`common.langs.${this.data.citizen.language}`);
        }
        const denyDialog = this.dialog.open(UserRequestDenyDialogComponent, {
            autoFocus: false,
            width: '680px',
            data: {
                title: this.translateService.instant('pages.residences.reviewDialog.rejectButton'),
                body: {
                    label: this.translateService.instant('pages.residences.reviewDialog.rejectReasonLabel', { lang: langLabel }),
                    placeholder: this.translateService.instant('pages.residences.reviewDialog.rejectReasonPlaceholder'),
                    error: this.translateService.instant('pages.residences.reviewDialog.notesRequired'),
                    button: this.translateService.instant('pages.residences.reviewDialog.confirmReject'),
                    presetTab: this.translateService.instant('pages.residences.reviewDialog.presetTab'),
                    customTab: this.translateService.instant('pages.residences.reviewDialog.customTab'),
                },
                denialReasons: this.denialReasons,
            },
        });

        denyDialog.componentInstance.onSubmit.pipe(takeUntil(this.destroy$)).subscribe((reason) => {
            denyDialog.componentInstance.dialogRef.close();
            this.loading = true;
            this.submitReview({ status: 'REJECTED', notes: reason });
        });
    }

    closeDialog(): void {
        this.dialogRef.close(false);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
